import type { LessonProgress } from "./types";

const STORAGE_KEY = "academy-progress";

function readAll(): Record<string, LessonProgress> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeAll(map: Record<string, LessonProgress>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

/** Merge server progress into local storage (server wins for completed lessons). */
export function mergeServerProgress(serverList: LessonProgress[]) {
  const local = readAll();
  for (const sp of serverList) {
    const existing = local[sp.lessonId];
    if (!existing || sp.completed || sp.lastStage > (existing.lastStage ?? 0)) {
      local[sp.lessonId] = sp;
    }
  }
  writeAll(local);
  return local;
}

/** Get all progress as a map (keyed by lessonId). */
export function getProgress(): Record<string, LessonProgress> {
  return readAll();
}

/** Mark a lesson as started. */
export function markStarted(lessonId: string) {
  const map = readAll();
  if (!map[lessonId]) {
    map[lessonId] = {
      lessonId,
      completed: false,
      quizScore: 0,
      quizAnswer: null,
      lastStage: 0,
      startedAt: new Date().toISOString(),
      completedAt: null,
    };
    writeAll(map);
  }
  return map[lessonId];
}

/** Update last stage reached. */
export function markStage(lessonId: string, stage: number) {
  const map = readAll();
  const existing = map[lessonId] ?? markStarted(lessonId);
  if (stage > (existing.lastStage ?? 0)) {
    existing.lastStage = stage;
    map[lessonId] = existing;
    writeAll(map);
  }
}

/** Mark a lesson as completed with quiz results. */
export function markCompleted(
  lessonId: string,
  quizAnswer: number,
  quizScore: number,
) {
  const map = readAll();
  const existing = map[lessonId] ?? markStarted(lessonId);
  existing.completed = true;
  existing.quizAnswer = quizAnswer;
  existing.quizScore = quizScore;
  existing.completedAt = new Date().toISOString();
  map[lessonId] = existing;
  writeAll(map);
  return map;
}
